<template>
	<!-- Touch (iPad/iPhone): render the search field + an INLINE results list in normal document flow.
	     A floating Vuetify autocomplete overlay renders behind the on-screen keyboard on iPad landscape
	     (LSP-16443); the keyboard-safe-menu band-aid from PR #2240 made it vanish entirely. This mirrors
	     the inline approach already shipped for the sibling address-lookup ticket (LSP-16445). -->
	<!-- Single root so parent fallthrough attributes (e.g. class="flex-grow-1" from FormPage) still apply
	     on touch, matching the desktop BaseAutoComplete branch. A bare <template> fragment would drop them. -->
	<!-- @focusout (which bubbles, unlike @blur) is the single close arm for the whole control: it fires
	     no matter which descendant lost focus — input, caret, or a focusable row inside the results
	     list — so focus can never leave the picker with the list stranded open (break-it round 2). -->
	<div v-if="isTouchInput" ref="touchRootRef" class="lucky-pack-touch" @focusout="handleTouchFocusOut">
		<v-text-field
			ref="touchFieldRef"
			data-test-id="spark-search-input"
			:class="{ 'lucky-pack-caret-open': showTouchResults }"
			:density="props.density"
			:loading="isLoading"
			:model-value="searchTerm"
			clearable
			@focus="handleFocus"
			@blur="handleTouchBlur"
			@click:control="handleTouchFieldClick"
			@update:model-value="handleTouchSearchInput"
			v-bind="searchFieldProps(searchTerm)">
			<template #label><FieldLabel label="Run this Spark (Optional)" /></template>
			<!-- LSP-19032: hand-built caret instead of Vuetify's `append-inner-icon`. Vuetify's InputIcon
			     binds only onClick/onKeydown, so a tap's mousedown still bubbled to .v-field, whose
			     onControlMousedown focuses the input — the resulting @focus opened the list and the
			     icon's own click then toggled it straight back shut, costing the user a whole tap.
			     @mousedown.stop.prevent keeps the field from focusing on mousedown so only the click
			     handler runs. Mirrors ProjectAutoComplete's touch caret (LSP-17690). -->
			<template #append-inner>
				<span
					v-test="'spark-touch-caret'"
					class="lucky-pack-touch-caret"
					role="button"
					tabindex="0"
					:aria-expanded="showTouchResults"
					aria-label="Toggle Spark list"
					@mousedown.stop.prevent
					@click.stop="toggleTouchList"
					@keydown.enter.stop.prevent="toggleTouchList"
					@keydown.space.stop.prevent="toggleTouchList">
					<v-icon aria-hidden="true">icon-caret-down</v-icon>
				</span>
			</template>
		</v-text-field>
		<div
			v-if="showTouchResults"
			data-test-id="spark-touch-results"
			class="lucky-pack-results-list mt-1"
			:class="{ 'lucky-pack-results-loading': isLoading }">
			<v-list density="compact">
				<template v-for="pack in itemsWithSelection" :key="pack.id">
					<v-tooltip
						v-if="disableExtraInput && requiresExtraInput(pack)"
						text="This spark can only be run from the My Stuff / Sessions or Projects page."
						location="top"
						scroll-strategy="close">
						<template #activator="{ props: tooltipProps }">
							<!-- Same @mousedown.prevent as the interactive rows below: tapping a disabled Spark is how a
							     touch user reveals this tooltip, and without it that tap blurs the field and collapses the
							     whole list out from under them (break-it round 2). -->
							<div v-bind="tooltipProps" @mousedown.prevent>
								<v-list-item data-test-id="spark-pack-item" disabled :title="pack.name">
									<v-list-item-subtitle v-if="pack.description">{{ pack.description }}</v-list-item-subtitle>
								</v-list-item>
							</div>
						</template>
					</v-tooltip>
					<v-list-item
						v-else
						data-test-id="spark-pack-item"
						:active="isSelected(pack)"
						:title="pack.name"
						@mousedown.prevent
						@click="handleTouchItemClick(pack)">
						<v-list-item-subtitle v-if="pack.description">{{ pack.description }}</v-list-item-subtitle>
					</v-list-item>
				</template>
				<v-list-item v-if="!isLoading && itemsWithSelection.length === 0" :title="noDataText" />
				<div
					v-if="hasMore && !isLoadingMore"
					v-intersect="{
						handler: onIntersect,
						options: { threshold: 0.1 }
					}"
					class="d-flex justify-center py-2" />
				<div v-if="isLoadingMore" class="d-flex justify-center py-2">
					<v-progress-circular indeterminate color="primary" size="20" />
				</div>
			</v-list>
		</div>
	</div>
	<!-- Desktop: keep the Vuetify autocomplete overlay. The default downward menu was never the problem
	     on desktop, where there is no on-screen keyboard shrinking the viewport. -->
	<BaseAutoComplete
		v-else
		:items="itemsWithSelection"
		:loading="isLoading"
		:density="props.density"
		item-title="name"
		item-value="id"
		:return-object="true"
		label="Run this Spark (Optional)"
		:no-data-text="noDataText"
		clearable
		@focus="handleFocus"
		@update:model-value="(pack: PackWithUi | null) => emit('update:modelValue', pack)"
		@update:search="handleSearchInput"
		v-bind="searchFieldProps(searchTerm)">
		<template #item="{ props: itemProps, item }">
			<v-tooltip
				v-if="disableExtraInput && requiresExtraInput(item.raw)"
				text="This spark can only be run from the My Stuff / Sessions or Projects page."
				location="top"
				:disabled="false"
				scroll-strategy="close">
				<template #activator="{ props: tooltipProps }">
					<div v-bind="tooltipProps">
						<v-list-item v-bind="itemProps" disabled :title="item.raw.name">
							<v-list-item-subtitle v-if="item.raw.description">{{ item.raw.description }}</v-list-item-subtitle>
						</v-list-item>
					</div>
				</template>
			</v-tooltip>
			<v-list-item
				v-else
				v-bind="{ ...itemProps, onClick: undefined }"
				:title="item.raw.name"
				@click="handleItemClick($event, itemProps, item.raw)">
				<v-list-item-subtitle v-if="item.raw.description">{{ item.raw.description }}</v-list-item-subtitle>
			</v-list-item>
		</template>
		<template #append-item>
			<div
				v-if="hasMore && !isLoadingMore"
				v-intersect="{
					handler: onIntersect,
					options: { threshold: 0.1 }
				}"
				class="d-flex justify-center py-2" />
			<div v-if="isLoadingMore" class="d-flex justify-center py-2">
				<v-progress-circular indeterminate color="primary" size="20" />
			</div>
		</template>
	</BaseAutoComplete>
</template>

<script setup lang="ts">
	import { searchFieldProps, isSearchTooLong as exceedsSearchLimit } from "@/helpers/searchLimit";
	import { ref, computed, watch, useAttrs } from "vue";
	import BaseAutoComplete from "@/components/formfields/BaseAutoComplete.vue";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { getPacksWithFilters } from "@/components/scribe/api";
	import type { PackWithUi } from "@/components/scribe/types";
	import { useSearchQuery } from "@/composables/useDebouncedSearch";
	import { useLatestRequest } from "@/composables/useLatestRequest";
	import DeviceUtility from "@/helpers/device";

	const attrs = useAttrs();
	const emit = defineEmits<{
		reselect: [pack: PackWithUi];
		"update:modelValue": [pack: PackWithUi | null];
	}>();

	// On touch devices the Vuetify autocomplete overlay menu renders behind the on-screen keyboard on
	// iPad landscape (LSP-16443). Render the search + results inline in document flow instead. Same gate
	// as AddressLookup's isTouchInput (LSP-16445).
	const isTouchInput = computed(() => DeviceUtility.isMobileOrTablet);

	function isSelected(pack: PackWithUi): boolean {
		const currentValue = attrs.modelValue as PackWithUi | null;
		return !!currentValue && currentValue.id === pack.id;
	}

	function handleItemClick(event: Event, itemProps: Record<string, any>, pack: PackWithUi) {
		if (isSelected(pack)) {
			event.preventDefault();
			event.stopPropagation();
			emit("reselect", pack);
			return;
		}

		itemProps.onClick?.(event);
	}

	// The inline touch list is open only while the user is browsing/searching. Selecting (or clearing)
	// collapses it and writes the chosen Spark's name into the field, so the touch path mirrors the
	// desktop v-autocomplete: list closes on pick and the selection is visible (LSP-16443).
	const touchListOpen = ref(false);

	// Vuetify's clearable field emits `null` from the clear (X) button and a string (incl. "") while
	// typing. Handle both here in one place so clearing deterministically closes the list and drops the
	// selection (desktop parity) — a separate @click:clear handler raced with this and reopened the list.
	function handleTouchSearchInput(value: string | null) {
		if (value === null) {
			emit("update:modelValue", null);
			setSearchSilent(""); // reset the query without re-triggering the debounced search/refetch
			// Drop the prior search results so a later refocus refetches fresh, unfiltered packs instead of
			// showing stale rows (loadPacks short-circuits while `loaded` is true). Abort any in-flight load
			// so a late response can't repopulate packs / re-set `loaded` after the clear.
			requestScope.abortCurrent();
			packs.value = [];
			loaded.value = false;
			touchListOpen.value = false;
			return;
		}
		touchListOpen.value = true;
		handleSearchInput(value);
	}

	// Inline-list counterpart of handleItemClick for the touch path: re-tapping the selected pack
	// re-runs it (reselect), otherwise the tap selects it via v-model. Disabled rows (Sparks that
	// require extra input when disableExtraInput is set) are non-interactive in the template.
	function handleTouchItemClick(pack: PackWithUi) {
		// While a fresh (non-append) search is in flight the inline list still shows the PREVIOUS query's
		// rows (kept to avoid layout jump). Those are stale relative to the typed text, so ignore taps until
		// the new results arrive — desktop empties its menu during reload, this is the inline equivalent.
		if (isLoading.value) return;
		// Either path collapses the list and reflects the (re)selected Spark's name in the field, mirroring
		// the desktop overlay which closes its menu on pick. setValueSilent avoids re-triggering the
		// debounced search (which would reopen the list and refetch).
		touchListOpen.value = false;
		setSearchSilent(pack.name);
		if (isSelected(pack)) {
			emit("reselect", pack);
			return;
		}
		emit("update:modelValue", pack);
	}

	const touchRootRef = ref<HTMLElement | null>(null);

	// Focus moving between the field's own parts — input to caret, caret back to input, or onto a
	// focusable row in the results list — is not the user leaving the control, so it must not
	// blur-close the list. Without this, a keyboard user could only ever OPEN the list: focusing the
	// caret to press Space/Enter blurred the input, which closed the list, so the keypress found it
	// closed and re-opened it (break-it round 1).
	function focusStaysWithinTouchField(event?: FocusEvent): boolean {
		const nextFocusTarget = event?.relatedTarget;
		return nextFocusTarget instanceof Node && !!touchRootRef.value?.contains(nextFocusTarget);
	}

	// The close arm for focus leaving the picker from ANY descendant. @blur does not bubble, so wiring
	// it per element only ever covers the elements someone remembered — round 2 stranded the list open
	// by tabbing out via a results row, which had no handler. focusout bubbles, so this one listener on
	// the root covers every current and future focusable descendant.
	function handleTouchFocusOut(event: FocusEvent) {
		if (focusStaysWithinTouchField(event)) return;
		handleTouchBlur();
	}

	// A tap on the field must ALWAYS open the list, not just the first one. After a caret-close the
	// input still holds focus, so no fresh @focus fires, handleFocus never runs, and the tap is
	// silently dead — the exact failure this component is being fixed for (break-it round 2).
	// handleFocus is idempotent while the list is open, so this cannot double-load on a cold tap,
	// where the focus-driven open has already run by the time the click lands.
	function handleTouchFieldClick() {
		handleFocus();
	}

	// Collapse the inline list when the field loses focus, matching the desktop overlay and the sibling
	// AddressLookup touch path. The interactive rows use @mousedown.prevent so tapping a result does not
	// blur-close the list before its @click selects.
	function handleTouchBlur(event?: FocusEvent) {
		if (focusStaysWithinTouchField(event)) return;
		touchListOpen.value = false;
		// Reconcile the field with the committed selection: a query the user typed but did not pick is
		// abandoned on blur so the field never shows text that does not match the bound Spark (desktop
		// parity). Reset `loaded` so a later refocus refetches for the reconciled query, not stale rows.
		const selectedName = (attrs.modelValue as PackWithUi | null)?.name ?? "";
		if (selectedName !== searchTerm.value) {
			setSearchSilent(selectedName);
			// Abort any in-flight load and drop the abandoned query's rows so a refocus refetches fresh
			// instead of opening the list with rows that no longer match the field (loadPacks short-circuits
			// while `loaded` is true, and on touch a non-append load no longer clears packs eagerly).
			requestScope.abortCurrent();
			packs.value = [];
			loaded.value = false;
		}
	}

	const props = withDefaults(
		defineProps<{
			disableExtraInput?: boolean;
			density?: "default" | "comfortable" | "compact";
		}>(),
		{
			disableExtraInput: true,
			density: "compact"
		}
	);

	function handleFocus() {
		// Touch only: toggleTouchList opens the list then programmatically focuses the field, and that
		// focus re-fires @focus -> handleFocus. Stay idempotent while the touch list is open so the
		// caret path issues a single loadPacks instead of two back-to-back runs (the first aborted).
		// Gated on isTouchInput so the shared desktop @focus path still reloads exactly as before
		// (desktop never resets touchListOpen, so an unconditional guard would wedge it open).
		if (isTouchInput.value && touchListOpen.value) return;
		touchListOpen.value = true;
		loadPacks(false);
	}

	const touchFieldRef = ref<{ focus: () => void } | null>(null);

	// Touch-only: tapping the dropdown caret toggles the inline list, mirroring the desktop
	// v-autocomplete (whose caret opens/closes its menu). Closing just collapses the list. Opening
	// calls handleFocus() to open + load packs (deterministic — focus() is a no-op when the field is
	// already focused, e.g. reopening after a caret-close, so it cannot be relied on to open) AND
	// focuses the field so the keyboard comes up and the @blur (handleTouchBlur) close path is armed,
	// so tapping elsewhere collapses the list instead of leaving it stuck open.
	//
	// LSP-19032: this is the ONLY thing a caret tap may run. The caret's @mousedown.stop.prevent is
	// what makes that true — without it .v-field's onControlMousedown focused the input first, and the
	// focus-driven open raced this toggle into closing the list the same tap had just opened.
	function toggleTouchList() {
		if (touchListOpen.value) {
			touchListOpen.value = false;
		} else {
			handleFocus();
			touchFieldRef.value?.focus();
		}
	}

	const requestScope = useLatestRequest();
	const {
		searchTerm,
		search: handleSearchInput,
		setValueSilent: setSearchSilent
	} = useSearchQuery({
		debounceMs: 300,
		searchCallback: () => {
			loaded.value = false;
			loadPacks(false);
		}
	});

	const noDataText = computed(() => {
		if (isLoading.value) {
			return "Loading Sparks...";
		}
		return searchTerm.value.trim() ? "No Sparks found" : "No reports available.";
	});

	function requiresExtraInput(pack: PackWithUi): boolean {
		if (!pack.tasks || pack.tasks.length === 0) {
			return false;
		}

		return pack.tasks.some((task) => task.type === "platform" || (task.type as string) === "user-input");
	}

	const PAGE_SIZE = 15;

	const packs = ref<PackWithUi[]>([]);
	const isLoading = ref(false);
	const isLoadingMore = ref(false);
	const loaded = ref(false);
	const nextCursor = ref<string | null>(null);
	const hasMore = computed(() => !!nextCursor.value);

	// Keep the touch field in sync with the bound selection: show a (pre-)selected Spark's name, and
	// clear the field when the parent clears v-model (e.g. FormPage drops the Spark) so it never shows a
	// stale name over a null value. Touch-only; on desktop the v-autocomplete renders the value itself.
	//
	// Declared here rather than beside the other touch handlers because `{ immediate: true }` runs it
	// during setup, and its body reads the request state declared just above.
	watch(
		() => attrs.modelValue as PackWithUi | null,
		(selected) => {
			if (!isTouchInput.value) return;
			const selectedName = selected?.name ?? "";
			if (selectedName === searchTerm.value) return;
			setSearchSilent(selectedName);
			// Same reconcile the clear (X) and blur paths perform: the field's text just changed out from
			// under whatever query produced the current rows, so those rows are stale. Without this a
			// parent-driven clear while the list is open (e.g. FormPage resetting the form) left the
			// abandoned query's filtered results cached, and a caret close + reopen short-circuited
			// loadPacks and re-showed them under an empty field (break-it round 2).
			requestScope.abortCurrent();
			loaded.value = false;
			// nextCursor belongs to the rows this reconcile invalidates. Only a non-append loadPacks clears
			// it, so leaving it set kept the infinite-scroll sentinel armed with page 2 of a query that no
			// longer matches the field (break-it round 3).
			nextCursor.value = null;
			if (touchListOpen.value) {
				// The list is on screen and the user is mid-browse: they never asked for it to empty, so
				// refetch for the rewritten query and let loadPacks swap the rows when the response lands.
				// Deliberately NOT clearing `packs` here — the touch list is inline in document flow, so
				// emptying it collapses its height and springs back, which is the same visible layout jump
				// loadPacks itself avoids on every other touch refetch (break-it rounds 3 and 4). A failed
				// refetch still clears the rows via loadPacks' own catch, so no stale-row hole opens up.
				loadPacks(false);
			} else {
				// Closed: there is nothing on screen to protect, and dropping the rows is what stops the
				// next open from short-circuiting and re-showing the abandoned query's results under the
				// rewritten field text (break-it round 2).
				packs.value = [];
			}
		},
		{ immediate: true }
	);

	// Always include the currently-selected pack in the items list, even
	// if it's not in the latest API response. Vuetify 3.12.x emits an
	// empty `update:search` event at blur time which our debounced
	// callback forwards as a no-search reload; if the selected pack
	// isn't on page 1 of the unfiltered result, it would otherwise fall
	// out of `items` and v-autocomplete may fall back to rendering the
	// id instead of the name. Guarding via the computed sidesteps the
	// Vuetify event-order quirk entirely.
	const itemsWithSelection = computed<PackWithUi[]>(() => {
		const selected = attrs.modelValue as PackWithUi | null | undefined;
		if (!selected || !selected.id) return packs.value;
		if (packs.value.some((p) => p.id === selected.id)) return packs.value;
		return [selected, ...packs.value];
	});

	// The inline list is shown whenever it is open (focus/typing); selecting, blurring, clearing, or an
	// untouched field keep it collapsed. Gating only on `touchListOpen` means the container stays mounted
	// through loading, results, the empty/no-data row, AND a failed fetch (loaded stays false + packs
	// cleared) — so a fetch error still surfaces the no-data row instead of silently unmounting the list.
	const showTouchResults = computed(() => touchListOpen.value);

	async function loadPacks(append = false, _event?: Event) {
		if (exceedsSearchLimit(searchTerm.value)) return;
		if (append && isLoadingMore.value) return;
		if (!append && loaded.value) return;
		const { requestId } = requestScope.startRequest();
		if (append) {
			isLoadingMore.value = true;
		} else {
			isLoading.value = true;
			nextCursor.value = null;
			// A new non-append load supersedes any in-flight pagination, so clear the bottom-spinner state
			// eagerly. The superseded append's `finally` won't reset it (its `!isSuperseded` check is false
			// once superseded), so without this the footer spinner sticks for the duration of the new load.
			// This must run on BOTH touch and desktop.
			isLoadingMore.value = false;
			// Clear stale items so the dropdown doesn't briefly show results from a prior query while the
			// new request is in flight (e.g. user types "rev", then "claude" — without this, "Revenue"
			// items remain selectable during the 300ms+ debounce + round-trip). `:loading` covers the gap.
			//
			// Desktop only: the floating overlay menu can show stale rows without disturbing layout. On
			// touch the list is inline in document flow, so emptying it on every keystroke collapses its
			// height and produces a visible layout jump — keep the prior rows until the new page arrives
			// (they swap in place). Transitions that change context (clear / blur-reconcile) clear packs
			// explicitly so they never show cross-query stale rows on refocus.
			if (!isTouchInput.value) {
				packs.value = [];
			}
		}
		try {
			const previousCursor = nextCursor.value;
			const trimmed = searchTerm.value.trim();
			const result = await getPacksWithFilters({
				cursor: nextCursor.value || undefined,
				limit: PAGE_SIZE,
				sortBy: "position",
				sortOrder: "asc",
				hasRealTimeMedia: false,
				search: trimmed || undefined
			});
			if (!requestScope.isCurrent(requestId)) return;
			const incoming = result.data ?? [];
			if (append) {
				const existingIds = new Set(packs.value.map((p) => p.id));
				const unique = incoming.filter((p) => !existingIds.has(p.id));
				packs.value = [...packs.value, ...unique];
				nextCursor.value = unique.length === 0 || result.nextCursor === previousCursor ? null : (result.nextCursor ?? null);
			} else {
				packs.value = incoming;
				nextCursor.value = result.nextCursor ?? null;
				loaded.value = true;
			}
		} catch (e) {
			if (!requestScope.isCurrent(requestId)) return;
			console.error("Failed to load Sparky packs", e);
			// On a non-append failure (initial load or new search), clear the
			// stale items so users can't pick a Spark from a previous query
			// while the input reflects a new one, and reset `loaded` so the
			// next focus retries instead of short-circuiting via the
			// `if (!append && loaded.value) return` guard. Append failures
			// leave existing pages intact — the next scroll-driven intersect
			// will retry page N+1 naturally.
			if (!append) {
				packs.value = [];
				loaded.value = false;
			}
		} finally {
			if (!requestScope.isSuperseded(requestId)) {
				isLoading.value = false;
				isLoadingMore.value = false;
			}
		}
	}

	function onIntersect(isIntersecting: boolean) {
		if (isIntersecting && hasMore.value && !isLoadingMore.value) {
			loadPacks(true);
		}
	}
</script>

<style scoped>
	/* Inline touch results (LSP-16443). Use dvh so the list shrinks above the on-screen keyboard on
	   iPad/iPhone instead of extending behind it the way a vh-sized box or floating overlay would. */
	.lucky-pack-results-list {
		overflow-y: auto;
		max-height: min(18rem, calc(100dvh - 16rem));
		border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
		background: rgb(var(--v-theme-surface));
		border-radius: 8px;
		box-shadow:
			0 5px 5px -3px rgba(0, 0, 0, 0.2),
			0 8px 10px 1px rgba(0, 0, 0, 0.14),
			0 3px 14px 2px rgba(0, 0, 0, 0.12);
	}

	.lucky-pack-results-loading {
		opacity: 0.6;
		pointer-events: none;
	}

	/* Vuetify styles its own append-inner icon as clickable; the hand-built caret (LSP-19032) has to
	   say so itself. */
	.lucky-pack-touch-caret {
		cursor: pointer;
	}

	/* Rotate the dropdown caret ▾ → ▴ while the inline list is open, matching the desktop
	   v-autocomplete (LSP-16443 rework). Scoped to .lucky-pack-touch AND targeted at the caret glyph
	   (.icon-caret-down) specifically so it can never rotate a sibling append-inner icon. These rules
	   only animate rotation — caret colour is intentionally left to Vuetify's default. */
	.lucky-pack-touch :deep(.v-field__append-inner .icon-caret-down) {
		transition: transform 0.2s ease;
	}

	.lucky-pack-touch .lucky-pack-caret-open :deep(.v-field__append-inner .icon-caret-down) {
		transform: rotate(180deg);
	}
</style>
